import React from "react";
import { formatMoney } from "../pricing";

interface RefundProgressBannerProps {
  amount: number;
  invoiceNumber: string;
}

/**
 * A refund that has been asked for and not yet confirmed.
 *
 * It stands where the plan card stands, because for these few minutes it is
 * the whole answer to "what am I paying for" — the plan is on its way out and
 * drawing it as though nothing had happened would be the screen disagreeing
 * with the dialog somebody just closed.
 *
 * Amber rather than red: nothing has gone wrong, and nothing is being asked
 * of the reader. It is the colour this app uses for a thing in flight.
 */
const RefundProgressBanner: React.FC<RefundProgressBannerProps> = ({
  amount,
  invoiceNumber,
}) => (
  <section
    aria-labelledby="refund-progress-heading"
    // Announced when it arrives: this replaces a card somebody was just
    // looking at, and a reader that hears nothing is left on the old one.
    // `aria-live` rather than `role="status"`, which would take the section's
    // own landmark role with it.
    aria-live="polite"
    className="rounded-md border border-[#F3D9AE] bg-[#FEF3E2] px-5 py-4 3xl:px-6 3xl:py-5"
  >
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0">
        <h2
          id="refund-progress-heading"
          className="text-[15px] 3xl:text-[16px] font-medium text-[#7A4E00]"
        >
          Refund in progress
        </h2>
        <p className="mt-1 text-[13px] 3xl:text-[14px] leading-[1.5] text-[#8A6320]">
          We&apos;re processing your refund with your bank. This usually takes a
          few seconds to confirm.
        </p>
      </div>

      <div className="text-right">
        <p className="text-[16px] 3xl:text-[18px] font-semibold text-[#7A4E00]">
          {formatMoney(amount)}
        </p>
        <p className="mt-0.5 text-[13px] 3xl:text-[14px] text-[#8A6320]">
          {invoiceNumber}
        </p>
      </div>
    </div>
  </section>
);

export default RefundProgressBanner;
